import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import {
  aiApi,
  LunaChatRequest,
  LunaChatResponse,
} from '@/api/endpoints/ai.api';

// Query keys
export const aiKeys = {
  all: ['ai'] as const,
  luna: () => [...aiKeys.all, 'luna'] as const,
  lunaChat: (patientId: string, conversationId?: string) =>
    [...aiKeys.luna(), 'chat', patientId, conversationId] as const,
  propensity: () => [...aiKeys.all, 'propensity'] as const,
  propensityScore: (patientId: string) => [...aiKeys.propensity(), patientId] as const,
  predictions: () => [...aiKeys.all, 'predictions'] as const,
  allPredictions: (patientId: string) => [...aiKeys.predictions(), 'all', patientId] as const,
  hospitalization: (patientId: string) => [...aiKeys.predictions(), 'hospitalization', patientId] as const,
  churn: (patientId: string) => [...aiKeys.predictions(), 'churn', patientId] as const,
  health: () => [...aiKeys.all, 'health'] as const,
};

// ==================== Luna Hooks ====================

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  alertTriggered?: boolean;
  alertMessage?: string;
}

/**
 * Hook para gerenciar conversas com a Luna.
 */
export function useLunaChat(patientId: string | undefined) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  const chatMutation = useMutation({
    mutationFn: (message: string) => {
      if (!patientId) throw new Error('Patient ID is required');
      const request: LunaChatRequest = {
        message,
        conversationId: conversationId || undefined,
      };
      return aiApi.chat(patientId, request);
    },
    onMutate: (message) => {
      // Adiciona mensagem do usuario imediatamente
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: message,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsTyping(true);
    },
    onSuccess: (response: LunaChatResponse) => {
      // Atualiza conversation ID se for nova conversa
      if (!conversationId) {
        setConversationId(response.conversationId);
      }

      // Adiciona resposta da Luna
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        suggestions: response.suggestions,
        alertTriggered: response.alertTriggered,
        alertMessage: response.alertMessage,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsTyping(false);
    },
    onError: () => {
      setIsTyping(false);
      // Adiciona mensagem de erro
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Desculpe, ocorreu um erro ao processar sua mensagem. Por favor, tente novamente.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    },
  });

  const sendMessage = useCallback(
    (message: string) => {
      if (message.trim()) {
        chatMutation.mutate(message);
      }
    },
    [chatMutation]
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
  }, []);

  return {
    messages,
    conversationId,
    isTyping,
    sendMessage,
    clearChat,
    isLoading: chatMutation.isPending,
    error: chatMutation.error,
  };
}

/**
 * Hook para check-in com a Luna.
 */
export function useLunaCheckIn() {
  return useMutation({
    mutationFn: ({ patientId, checkInType }: { patientId: string; checkInType?: string }) =>
      aiApi.checkIn(patientId, checkInType),
  });
}

// ==================== Propensity Hooks ====================

/**
 * Hook para buscar propensity score de um paciente.
 */
export function usePropensityScore(patientId: string | undefined) {
  return useQuery({
    queryKey: aiKeys.propensityScore(patientId!),
    queryFn: () => aiApi.getPropensityScore(patientId!),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para calcular propensity scores em lote.
 */
export function useBatchPropensityScores() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patientIds: string[]) => aiApi.getBatchPropensityScores(patientIds),
    onSuccess: (results) => {
      // Atualiza cache individual para cada paciente
      results.forEach((result) => {
        queryClient.setQueryData(aiKeys.propensityScore(result.patientId), result);
      });
    },
  });
}

// ==================== Prediction Hooks ====================

/**
 * Hook para buscar todas as predicoes de um paciente.
 */
export function useAllPredictions(patientId: string | undefined) {
  return useQuery({
    queryKey: aiKeys.allPredictions(patientId!),
    queryFn: () => aiApi.getAllPredictions(patientId!),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
}

/**
 * Hook para buscar predicao de hospitalizacao.
 */
export function useHospitalizationPrediction(patientId: string | undefined) {
  return useQuery({
    queryKey: aiKeys.hospitalization(patientId!),
    queryFn: () => aiApi.getHospitalizationPrediction(patientId!),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Hook para buscar predicao de churn.
 */
export function useChurnPrediction(patientId: string | undefined) {
  return useQuery({
    queryKey: aiKeys.churn(patientId!),
    queryFn: () => aiApi.getChurnPrediction(patientId!),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000,
  });
}

// ==================== Health Hook ====================

/**
 * Hook para verificar saude do servico de IA.
 */
export function useAIHealth() {
  return useQuery({
    queryKey: aiKeys.health(),
    queryFn: () => aiApi.healthCheck(),
    staleTime: 30 * 1000, // 30 segundos
    refetchInterval: 60 * 1000, // Refetch a cada minuto
  });
}

// ==================== Utility Functions ====================

export function getPropensityLevelColor(level: string): string {
  switch (level) {
    case 'VERY_HIGH':
      return 'text-purple-600 bg-purple-100';
    case 'HIGH':
      return 'text-red-600 bg-red-100';
    case 'MEDIUM':
      return 'text-yellow-600 bg-yellow-100';
    case 'LOW':
    default:
      return 'text-green-600 bg-green-100';
  }
}

export function getPropensityLevelLabel(level: string): string {
  switch (level) {
    case 'VERY_HIGH':
      return 'Muito Alta';
    case 'HIGH':
      return 'Alta';
    case 'MEDIUM':
      return 'Media';
    case 'LOW':
    default:
      return 'Baixa';
  }
}

export function getRiskLevelColor(level: string): string {
  switch (level) {
    case 'VERY_HIGH':
      return 'text-purple-600 bg-purple-100';
    case 'HIGH':
      return 'text-red-600 bg-red-100';
    case 'MODERATE':
      return 'text-yellow-600 bg-yellow-100';
    case 'LOW':
      return 'text-green-600 bg-green-100';
    case 'VERY_LOW':
    default:
      return 'text-blue-600 bg-blue-100';
  }
}

export function getRiskLevelLabel(level: string): string {
  switch (level) {
    case 'VERY_HIGH':
      return 'Muito Alto';
    case 'HIGH':
      return 'Alto';
    case 'MODERATE':
      return 'Moderado';
    case 'LOW':
      return 'Baixo';
    case 'VERY_LOW':
    default:
      return 'Muito Baixo';
  }
}

export function getTrendIcon(trend: string): string {
  switch (trend) {
    case 'IMPROVING':
      return '↓';
    case 'WORSENING':
      return '↑';
    case 'STABLE':
    default:
      return '→';
  }
}

export function getTrendColor(trend: string): string {
  switch (trend) {
    case 'IMPROVING':
      return 'text-green-600';
    case 'WORSENING':
      return 'text-red-600';
    case 'STABLE':
    default:
      return 'text-gray-600';
  }
}
